import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pdApi } from '../api';
import '../screens.css';
import './Optimizer.css';
import './Compare.css';
import { HelpPanel } from '../HelpPanel';
import { OPT_COMPARE_SCENARIOS } from '../helpContent';

/**
 * Portfolio Optimizer → Saved Scenarios → Compare Scenarios.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/optimizer/compare-scenarios.ejs`:
 * the same seventeen metric rows in the same order, the same wording, the same sticky head
 * and sticky first column, the same weight bars and yes/no badges, and the same ids-in-the-
 * query-string contract so a comparison stays a link someone can send.
 *
 * A SUB-PATH of Saved Scenarios rather than its own nav row, because that is where it is
 * reached from and where the owner asked for it. The source is a separate page in a server-
 * rendered app with no menu entry either.
 *
 * WHAT IS NEW: the charts. The source compares on numbers alone; the owner asked for the
 * comparison to work "on data and graph capabilities", so the block above the table draws
 * the four questions a reader actually asks — how big is each portfolio, which returns best,
 * which pays back soonest, and how much of each budget is consumed — plus an OVERLAP grid
 * showing which opportunities every scenario funds and which are unique to one. That last
 * one is the question the source cannot answer at all, and it is usually the deciding one.
 *
 * A SCENARIO IS A SNAPSHOT. Every figure here was copied onto the scenario when it was
 * saved; none of it is recomputed. Two scenarios saved a month apart are therefore compared
 * on the numbers as they stood then, which is the point of having saved them.
 */

type Dict = Record<string, unknown>;

const n = (v: unknown) => (v === null || v === undefined ? 0 : Number(v));
const money = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const pct1 = (v: unknown) => `${(n(v) * 100).toFixed(1)}%`;
const shortMoney = (v: number) =>
  Math.abs(v) >= 1_000_000
    ? `$${(v / 1_000_000).toFixed(Math.abs(v) >= 10_000_000 ? 0 : 1)}M`
    : Math.abs(v) >= 1000
      ? `$${Math.round(v / 1000)}k`
      : `$${Math.round(v)}`;
const yn = (v: unknown) =>
  v ? <span className="cmp-badge cmp-badge-yes">Yes</span> : <span className="cmp-badge cmp-badge-no">No</span>;

/* The source's palette, used by both the table and the charts so there is one language. */
const C = {
  cost: '#f57c00',
  benefit: '#2e7d32',
  budget: '#90a4ae',
  primary: '#1976d2',
  bad: '#c62828',
  grid: '#eceff1',
  ink: '#424242',
  muted: '#757575',
};

const weightRow = (v: unknown) => {
  const p = Math.round(n(v) * 100);
  return (
    <div className="cmp-weight-bar">
      <div className="cmp-weight-fill" style={{ width: `${p}%` }} />
      <div className="cmp-weight-text">{p}%</div>
    </div>
  );
};

/** Grouped bars on ONE scale — cost, benefit and the budget each scenario was held to. */
function SizeChart({ rows }: { rows: Dict[] }) {
  const W = 480, rowH = 46, T = 6, H = T + rows.length * rowH + 18;
  const L = 124, Rr = 64;
  const iw = W - L - Rr;
  const max = Math.max(
    1,
    ...rows.flatMap((s) => [n(s.total_cost_selected), n(s.total_benefit_selected), n(s.budget_constraint)]),
  );
  const bars: [string, string, (s: Dict) => number][] = [
    ['Benefit', C.benefit, (s) => n(s.total_benefit_selected)],
    ['Cost', C.cost, (s) => n(s.total_cost_selected)],
    ['Budget', C.budget, (s) => n(s.budget_constraint)],
  ];
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Benefit, cost and budget by scenario">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={L + iw * f} x2={L + iw * f} y1={T} y2={T + rows.length * rowH} stroke={C.grid} strokeWidth={1} />
            <text x={L + iw * f} y={H - 5} textAnchor="middle" style={{ font: '500 9.5px sans-serif', fill: C.muted }}>
              {shortMoney(max * f)}
            </text>
          </g>
        ))}
        {rows.map((s, i) => (
          <g key={String(s.scenario_id)}>
            <text x={0} y={T + i * rowH + 16} style={{ font: '600 11px sans-serif', fill: C.ink }}>
              {String(s.scenario_name).slice(0, 18)}
            </text>
            {bars.map(([, colour, get], b) => {
              const v = get(s);
              const y = T + i * rowH + 6 + b * 11;
              return (
                <g key={b}>
                  <rect x={L} y={y} width={Math.max(1, (v / max) * iw)} height={8} rx={2} fill={colour} />
                  {b === 0 ? (
                    <text x={L + iw + 6} y={y + 7.5} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: C.ink }}>
                      {shortMoney(v)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
      <div className="cmp-legend">
        {bars.map(([label, colour]) => (
          <span key={label}><i style={{ background: colour }} />{label}</span>
        ))}
      </div>
    </>
  );
}

/** One measure, ranked. Used for ROI, payback and budget utilisation. */
function RankChart({
  rows, get, label, fmt, lowerIsBetter = false, colour = C.primary,
}: {
  rows: Dict[];
  get: (s: Dict) => number;
  label: string;
  fmt: (v: number) => string;
  lowerIsBetter?: boolean;
  colour?: string;
}) {
  const live = rows.filter((s) => Number.isFinite(get(s)));
  const W = 480, rowH = 28, H = live.length * rowH + 4, L = 124, Rr = 70;
  const iw = W - L - Rr;
  const max = Math.max(1, ...live.map((s) => Math.abs(get(s))));
  const ordered = live.slice().sort((a, b) => (lowerIsBetter ? get(a) - get(b) : get(b) - get(a)));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      {ordered.map((s, i) => {
        const v = get(s);
        const y = i * rowH + 6;
        const neg = v < 0;
        return (
          <g key={String(s.scenario_id)}>
            <text x={0} y={y + 11} style={{ font: '600 11px sans-serif', fill: C.ink }}>
              {String(s.scenario_name).slice(0, 18)}
            </text>
            <rect x={L} y={y} width={Math.max(2, (Math.abs(v) / max) * iw)} height={14} rx={3}
              fill={neg ? C.bad : colour} />
            <text x={L + iw + 8} y={y + 11}
              style={{ font: "600 11px 'IBM Plex Mono',monospace", fill: neg ? C.bad : C.ink }}>
              {fmt(v)}
            </text>
            {i === 0 ? (
              <text x={L + iw + 8} y={y + 22} style={{ font: '600 9px sans-serif', fill: C.benefit }}>
                best
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export function CompareScenarios() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ids = useMemo(
    () => (params.get('ids') ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    [params],
  );

  const [scenarios, setScenarios] = useState<Dict[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /*
   * THE PAGE DOES NOT SCROLL. Heading and column heads are fixed; the comparison scrolls.
   * Measured, never `height: 100%` — `<main>` carries `min-height: 100vh` and grows with
   * its content, so a percentage resolves against nothing and the window scrolls instead.
   * Callback ref, because the body mounts after the loading gate.
   */
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!root) return;
    const main = root.closest('main');
    const measure = () => {
      const top = root.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [root]);

  useEffect(() => {
    void (async () => {
      if (!ids.length) { setLoading(false); return; }
      try {
        const results = await Promise.all(ids.map((id) => pdApi.optimizer.scenario(Number(id))));
        setScenarios(results.map((r) => r.scenario).filter(Boolean));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [ids]);

  /*
   * THE OVERLAP GRID. Every opportunity any scenario selected, with a mark per scenario
   * that funds it. Ordered by how many scenarios agree, so the portfolio they all share
   * sits at the top and the choices unique to one sit at the bottom — which is the shape
   * of the decision.
   */
  const overlap = useMemo(() => {
    /*
     * WHICH ROWS COUNT AS FUNDED.
     *
     * `selection_status` is LOWERCASE and the table's own check constraint fixes the
     * vocabulary at `selected | locked | excluded | not_selected`. Filtering on `'Selected'`
     * would have matched none of them and drawn an empty grid. The save loop writes only the
     * chosen set today, but the schema plainly anticipates rejected rows, so the two negative
     * statuses are excluded rather than assumed absent.
     *
     * A LOCKED pick is carried through and marked differently: it was pinned by hand, so it
     * is not evidence that the weights chose it.
     */
    const OUT = new Set(['excluded', 'not_selected']);
    const picked = new Map<string, { id: string; description: string; by: Map<number, string> }>();
    scenarios.forEach((s) => {
      ((s.opportunities as Dict[]) ?? [])
        .filter((o) => !OUT.has(String(o.selection_status ?? 'selected')))
        .forEach((o) => {
          const key = String(o.opportunity_id);
          const row = picked.get(key)
            ?? { id: key, description: String(o.description ?? ''), by: new Map<number, string>() };
          row.by.set(Number(s.scenario_id), String(o.selection_status ?? 'selected'));
          picked.set(key, row);
        });
    });
    const rows = [...picked.values()].sort(
      (a, b) => b.by.size - a.by.size || a.id.localeCompare(b.id),
    );
    return {
      rows,
      common: rows.filter((r) => r.by.size === scenarios.length).length,
      unique: rows.filter((r) => r.by.size === 1).length,
    };
  }, [scenarios]);

  const ROWS: { label: string; render: (s: Dict) => React.ReactNode; section?: string }[] = [
    { label: 'Description', render: (s) => (s.scenario_description ? String(s.scenario_description) : 'N/A') },
    {
      label: 'Created Date',
      render: (s) =>
        s.created_at
          ? new Date(String(s.created_at)).toLocaleString('en-US', {
              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })
          : '—',
    },
    { label: 'Budget Constraint', render: (s) => <span className="cmp-metric">{money(s.budget_constraint)}</span>,
      section: 'Portfolio' },
    { label: '# of Opportunities', render: (s) => <span className="cmp-metric">{String(n(s.count_selected))}</span> },
    { label: 'Total Cost', render: (s) => <span className="cmp-metric">{money(s.total_cost_selected)}</span> },
    { label: 'Total Benefit', render: (s) => <span className="cmp-metric">{money(s.total_benefit_selected)}</span> },
    {
      label: 'Portfolio ROI',
      render: (s) => (
        <span className={`cmp-metric ${n(s.portfolio_roi) > 0 ? 'cmp-positive' : 'cmp-negative'}`}>
          {pct1(s.portfolio_roi)}
        </span>
      ),
    },
    {
      label: 'Payback Period',
      render: (s) => (
        <span className="cmp-metric">
          {s.payback_months === null || s.payback_months === undefined
            ? 'N/A'
            : `${String(s.payback_months)} months`}
        </span>
      ),
    },
    { label: 'Budget Remaining', render: (s) => <span className="cmp-metric">{money(s.budget_remaining)}</span> },
    { label: 'Weight: ROI', render: (s) => weightRow(s.weight_roi), section: 'Weight strategy' },
    { label: 'Weight: Criticality', render: (s) => weightRow(s.weight_criticality) },
    { label: 'Weight: Alignment', render: (s) => weightRow(s.weight_alignment) },
    { label: 'Weight: Benefit/Cost', render: (s) => weightRow(s.weight_benefit_cost) },
    { label: 'Weight: Risk', render: (s) => weightRow(s.weight_risk) },
    { label: 'Feasible Only', render: (s) => yn(s.include_only_feasible), section: 'Constraints' },
    { label: 'Exclude High Risk', render: (s) => yn(s.exclude_high_risk) },
    { label: 'Respect Mandatory', render: (s) => yn(s.respect_mandatory) },
  ];

  return (
    <div className="pd-screen pd-screen--locked" ref={setRoot} style={rootH ? { height: rootH, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}>
      <div className="pd-page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="pd-page-header__title">Compare Scenarios</h1>
          <p className="pd-page-header__subtitle">
            {scenarios.length
              ? `Comparing ${scenarios.length} scenario${scenarios.length > 1 ? 's' : ''} — every figure is the snapshot taken when that scenario was saved.`
              : 'Side-by-side comparison of portfolio optimization scenarios'}
          </p>
        </div>
        <div className="pd-page-header__actions">
          <button type="button" className="pd-btn" onClick={() => navigate('..')}>
            ← Back to Saved Scenarios
          </button>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPT_COMPARE_SCENARIOS} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      <div className="cmp-body">
        {loading ? (
          <div className="cmp-empty">
            <div className="cmp-empty-icon">⚖️</div>
            <h3>Loading Scenarios…</h3>
            <p>Please wait while we fetch the comparison data</p>
          </div>
        ) : !ids.length ? (
          <div className="cmp-empty">
            <div className="cmp-empty-icon">❌</div>
            <h3>No Scenarios Selected</h3>
            <p>Please select scenarios from the Saved Scenarios page to compare</p>
          </div>
        ) : !scenarios.length ? (
          <div className="cmp-empty">
            <div className="cmp-empty-icon">❌</div>
            <h3>No Scenarios Found</h3>
            <p>The selected scenarios could not be loaded</p>
          </div>
        ) : (
          <>
            <div className="cmp-charts">
              <div className="cmp-chart">
                <h3>Portfolio size</h3>
                <p>Benefit and cost against the budget each scenario was held to, on one scale.</p>
                <SizeChart rows={scenarios} />
              </div>

              <div className="cmp-chart">
                <h3>Return</h3>
                <p>Portfolio ROI, ranked. Higher is better.</p>
                <RankChart rows={scenarios} label="Portfolio ROI by scenario"
                  get={(s) => n(s.portfolio_roi) * 100} fmt={(v) => `${v.toFixed(1)}%`}
                  colour={C.benefit} />
              </div>

              <div className="cmp-chart">
                <h3>Payback</h3>
                <p>Months to recover the portfolio&rsquo;s cost. Lower is faster.</p>
                {scenarios.some((s) => s.payback_months !== null && s.payback_months !== undefined) ? (
                  <RankChart rows={scenarios.filter((s) => s.payback_months !== null && s.payback_months !== undefined)}
                    label="Payback months by scenario" get={(s) => n(s.payback_months)}
                    fmt={(v) => `${Math.round(v)} mo`} lowerIsBetter colour={C.primary} />
                ) : (
                  <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                    No scenario in this set recorded a payback period.
                  </p>
                )}
              </div>

              <div className="cmp-chart">
                <h3>Budget used</h3>
                <p>Selected cost as a share of that scenario&rsquo;s own budget.</p>
                <RankChart rows={scenarios.filter((s) => n(s.budget_constraint) > 0)}
                  label="Budget utilisation by scenario"
                  get={(s) => (n(s.total_cost_selected) / n(s.budget_constraint)) * 100}
                  fmt={(v) => `${v.toFixed(0)}%`} colour={C.cost} />
              </div>

              <div className="cmp-chart" style={{ gridColumn: '1 / -1' }}>
                <h3>Where the scenarios agree</h3>
                <p>
                  {overlap.rows.length} opportunit{overlap.rows.length === 1 ? 'y' : 'ies'} selected by at
                  least one scenario · {overlap.common} funded by all {scenarios.length} ·{' '}
                  {overlap.unique} unique to a single scenario.
                </p>
                <div className="cmp-legend" style={{ marginTop: 0, marginBottom: 8 }}>
                  <span><i style={{ background: '#1976d2' }} />chosen by the optimizer</span>
                  <span><i style={{ background: '#7e57c2' }} />locked by hand</span>
                  <span><i style={{ background: '#eceff1' }} />not funded</span>
                </div>
                <div className="cmp-scroll">
                  <table className="cmp-overlap">
                    <thead>
                      <tr>
                        <th>Opportunity</th>
                        {scenarios.map((s) => (
                          <th key={String(s.scenario_id)}>{String(s.scenario_name).slice(0, 14)}</th>
                        ))}
                        <th>Agreement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overlap.rows.map((r) => (
                        <tr key={r.id}>
                          <td title={`${r.id} — ${r.description}`}>
                            <strong>{r.id}</strong>
                            {r.description ? ` · ${r.description}` : ''}
                          </td>
                          {scenarios.map((s) => {
                            const how = r.by.get(Number(s.scenario_id));
                            return (
                              <td key={String(s.scenario_id)}
                                title={how ? `${String(s.scenario_name)} — ${how}` : `Not funded by ${String(s.scenario_name)}`}>
                                <i className={
                                  !how ? 'cmp-dot cmp-dot--off'
                                  : how === 'selected' ? 'cmp-dot'
                                  : 'cmp-dot cmp-dot--forced'} />
                              </td>
                            );
                          })}
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            {r.by.size}/{scenarios.length}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <table className="cmp-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {scenarios.map((s) => (
                    <th key={String(s.scenario_id)}>
                      <div className="cmp-head">
                        <div className="cmp-head-name">{String(s.scenario_name)}</div>
                        <div className="cmp-head-project">{String(s.project_id)}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <Fragment key={row.label}>
                    {row.section ? (
                      <tr className="cmp-section">
                        <td colSpan={scenarios.length + 1}>{row.section}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td>{row.label}</td>
                      {scenarios.map((s) => (
                        <td key={String(s.scenario_id)}>{row.render(s)}</td>
                      ))}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
